import NiceModal from "@ebay/nice-modal-react";
import {
  detectClientEnvironment,
  type ClientEnvironment,
} from "../../clientEnv";
import {
  getCurrentCountryCode,
  getCurrentInstallLink,
} from "../../../utils/countryConfigUtils";
import { getCountryAsset } from "../../../utils/countryAssetUtils";
import {
  WITHDRAWAL_OFFICIAL_APP_PROMPT_STAGE,
  shouldContinueWithdrawalAfterPrompt,
  shouldShowWithdrawalOfficialAppPrompt,
} from "./policy";
import {
  buildWithdrawalOfficialAppSnoozeKey,
  readWithdrawalOfficialAppSnooze,
  type StorageLike,
} from "./storage";
import type {
  WithdrawalOfficialAppPromptModalProps,
  WithdrawalOfficialAppPromptOutcome,
  WithdrawalOfficialAppPromptStage,
} from "./types";

interface RunWithdrawalOfficialAppPromptOptions {
  stage: WithdrawalOfficialAppPromptStage;
  userId?: string | number | null;
  onContinue?: () => void | Promise<void>;
}

export interface WithdrawalOfficialAppPromptControllerDependencies {
  configuredStage: WithdrawalOfficialAppPromptStage;
  detectEnvironment: () => ClientEnvironment;
  getInstallLink: () => string;
  getCountryCode: () => string | null;
  getHero: () => string;
  showModal: (
    props: WithdrawalOfficialAppPromptModalProps,
  ) => Promise<WithdrawalOfficialAppPromptOutcome>;
  storage?: StorageLike;
}

const showDefaultModal = async (
  props: WithdrawalOfficialAppPromptModalProps,
): Promise<WithdrawalOfficialAppPromptOutcome> => {
  const { default: WithdrawalOfficialAppPromptModal } = await import(
    "./WithdrawalOfficialAppPromptModal"
  );

  return NiceModal.show(
    WithdrawalOfficialAppPromptModal,
    props,
  ) as Promise<WithdrawalOfficialAppPromptOutcome>;
};

const DEFAULT_DEPENDENCIES: WithdrawalOfficialAppPromptControllerDependencies = {
  configuredStage: WITHDRAWAL_OFFICIAL_APP_PROMPT_STAGE,
  detectEnvironment: detectClientEnvironment,
  getInstallLink: getCurrentInstallLink,
  getCountryCode: getCurrentCountryCode,
  getHero: () => getCountryAsset("withdrawOfficialAppHero"),
  showModal: showDefaultModal,
};

let activeRun: Promise<WithdrawalOfficialAppPromptOutcome> | null = null;

const continueWithdrawal = async (
  onContinue?: () => void | Promise<void>,
): Promise<WithdrawalOfficialAppPromptOutcome> => {
  await onContinue?.();
  return "continue";
};

export const runWithdrawalOfficialAppPrompt = (
  options: RunWithdrawalOfficialAppPromptOptions,
  dependencies: WithdrawalOfficialAppPromptControllerDependencies =
    DEFAULT_DEPENDENCIES,
): Promise<WithdrawalOfficialAppPromptOutcome> => {
  let env: ClientEnvironment;
  let installLink: string;
  let snoozeKey: string;
  let eligible: boolean;

  try {
    env = dependencies.detectEnvironment();
    installLink = dependencies.getInstallLink();
    const countryCode = dependencies.getCountryCode();
    snoozeKey = buildWithdrawalOfficialAppSnoozeKey({
      userId: options.userId,
      countryCode,
      installLink,
    });
    const isSnoozed = readWithdrawalOfficialAppSnooze(
      snoozeKey,
      dependencies.storage,
    );
    eligible = shouldShowWithdrawalOfficialAppPrompt({
      env,
      installLink,
      isSnoozed,
      requestedStage: options.stage,
      configuredStage: dependencies.configuredStage,
    });
  } catch (_error) {
    return continueWithdrawal(options.onContinue);
  }

  if (!eligible) {
    return continueWithdrawal(options.onContinue);
  }

  if (activeRun) {
    return activeRun;
  }

  const run = async (): Promise<WithdrawalOfficialAppPromptOutcome> => {
    let outcome: WithdrawalOfficialAppPromptOutcome;

    try {
      outcome = await dependencies.showModal({
        env,
        stage: options.stage,
        installLink,
        heroSrc: dependencies.getHero(),
        snoozeKey,
      });
    } catch (_error) {
      outcome = "continue";
    }

    if (shouldContinueWithdrawalAfterPrompt(options.stage, outcome)) {
      await options.onContinue?.();
    }

    return outcome;
  };

  activeRun = run().finally(() => {
    activeRun = null;
  });

  return activeRun;
};
